import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase as supabaseClient } from '@/integrations/supabase/client';
import { useBusinesses } from '@/hooks/business/useBusinesses';
import { Users, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from "@/hooks/ui/use-toast";
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { useBusinessTimeForDashboard } from '@/hooks/ui';
import { BusinessDetails } from '@/types/index';
import { getHolidayInfo, extractCountryFromAddress } from '@/lib/scheduling/holidays-simple';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';
import { getShiftStatusStyles, StyleVariant } from '@/lib/scheduling/shift-status-styles';
import { SHIFT_STATUS_CONFIG } from '@/lib/scheduling/shift-status-types';
import { ShiftStatusBadge } from '@/components/shifts/status/ShiftStatusBadge';
import { useShiftStatusService } from '@/hooks/scheduling/useShiftStatusService';

interface EmployeePresenceCardsProps {
  onClick?: () => void;
  businessDetails: BusinessDetails | null;
  businessStatus: {
    statusText: string;
    openUntil?: string;
    reopensAt?: string;
  } | null;
}

interface UnifiedAttendanceInfo {
  statusCounts: Record<ShiftStatus, number>;
  totalScheduled: number;
  totalPresent: number;
}

const EmployeePresenceCards: React.FC<EmployeePresenceCardsProps> = ({ onClick, businessDetails, businessStatus }) => {
  const { selectedBusinessId } = useBusinesses();
  const isMobile = useIsMobile();
  const shiftStatusService = useShiftStatusService();
  const [loggedInEmployees, setLoggedInEmployees] = useState<number | null>(null);
  const [scheduledEmployees, setScheduledEmployees] = useState<number | null>(null);
  const [attendanceInfo, setAttendanceInfo] = useState<UnifiedAttendanceInfo | null>(null);
  const [loading, setLoading] = useState(true);


  const [statusText, setStatusText] = useState('N/A');
  const [dotColorClass, setDotColorClass] = useState('bg-gray-400');

  // Use business timezone-aware time with proper formatting helpers
  const { formatTimeInBusinessTimezone, formatDateInBusinessTimezone } = useBusinessTimeForDashboard();

  // Format time and date using business timezone
  const currentTime = formatTimeInBusinessTimezone();
  const currentDate = formatDateInBusinessTimezone();
  const [todayHoliday, setTodayHoliday] = useState<string | null>(null);
  const [upcomingHoliday, setUpcomingHoliday] = useState<{
    name: string;
    daysAway: number;
  } | null>(null);

  useEffect(() => {
    const fetchEmployeePresence = async () => {
      if (!selectedBusinessId) {
        setLoggedInEmployees(0);
        setScheduledEmployees(0);
        setAttendanceInfo(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      const today = new Date();
      const dayStart = new Date(today.setHours(0, 0, 0, 0)).toISOString();
      const dayEnd = new Date(today.setHours(23, 59, 59, 999)).toISOString();

      try {
        // Fetch logged-in employees (unique arrivals today)
        const { data: arrivalLogs, error: loggedInError } = await supabaseClient
          .from('time_logs')
          .select('employee_id, scanned_at')
          .eq('business_id', selectedBusinessId)
          .eq('event_type', 'arrival')
          .gte('scanned_at', dayStart)
          .lte('scanned_at', dayEnd);

        if (loggedInError) {
          throw loggedInError;
        }

        // Fetch all shifts for today with detailed information
        const { data: shiftsData, error: shiftsError } = await supabaseClient
          .from('shifts')
          .select('employee_id, start_time, end_time')
          .eq('business_id', selectedBusinessId)
          .gte('start_time', dayStart)
          .lte('end_time', dayEnd);

        if (shiftsError) {
          throw shiftsError;
        }

        // Fetch departure logs for early departure tracking
        const { data: departureLogs, error: departureError } = await supabaseClient
          .from('time_logs')
          .select('employee_id, scanned_at')
          .eq('business_id', selectedBusinessId)
          .eq('event_type', 'departure')
          .gte('scanned_at', dayStart)
          .lte('scanned_at', dayEnd);

        if (departureError) {
          throw departureError;
        }

        // Process the data
        const uniqueArrivals = new Set<string>();
        const arrivalTimes = new Map<string, Date>();
        
        arrivalLogs?.forEach(log => {
          if (log.employee_id) {
            uniqueArrivals.add(log.employee_id);
            arrivalTimes.set(log.employee_id, new Date(log.scanned_at));
          }
        });

        const departureTimes = new Map<string, Date>();
        departureLogs?.forEach(log => {
          if (log.employee_id) {
            departureTimes.set(log.employee_id, new Date(log.scanned_at));
          }
        });

        // Use unified shift status service for attendance analysis
        const statusCounts: Record<ShiftStatus, number> = {
          [ShiftStatus.PENDING]: 0,
          [ShiftStatus.IN_PROGRESS]: 0,
          [ShiftStatus.COMPLETED]: 0,
          [ShiftStatus.COMPLETED_LATE]: 0,
          [ShiftStatus.MISSED]: 0,
          [ShiftStatus.UNASSIGNED]: 0,
          [ShiftStatus.ON_TIME]: 0,
          [ShiftStatus.LATE]: 0,
          [ShiftStatus.TOO_EARLY]: 0,
          [ShiftStatus.LEFT_EARLY]: 0,
          [ShiftStatus.OVERTIME]: 0,
          [ShiftStatus.NO_DEPARTURE_SCAN]: 0,
          [ShiftStatus.PLANNING]: 0,
          // Composite statuses
          [ShiftStatus.TOO_EARLY_OVERTIME]: 0,
          [ShiftStatus.TOO_EARLY_LEFT_EARLY]: 0,
          [ShiftStatus.LATE_OVERTIME]: 0,
          [ShiftStatus.LATE_LEFT_EARLY]: 0
        };
        
        const uniqueScheduledEmployees = new Set<string>();
        let totalPresent = 0;

        shiftsData?.forEach(shift => {
          if (shift.employee_id) {
            uniqueScheduledEmployees.add(shift.employee_id);
            const actualArrival = arrivalTimes.get(shift.employee_id);
            const actualDeparture = departureTimes.get(shift.employee_id);
            
            // Calculate unified shift status (using timezone-aware service)
            const shiftStatus = shiftStatusService.calculateShiftStatus(
              new Date(shift.start_time),
              new Date(shift.end_time),
              shift.employee_id,
              actualArrival?.toISOString() || null,
              actualDeparture?.toISOString() || null
            );
            
            statusCounts[shiftStatus]++;
            
            // Count as present if employee has arrived
            if (actualArrival) {
              totalPresent++;
            }
          }
        });

        setLoggedInEmployees(uniqueArrivals.size);
        setScheduledEmployees(uniqueScheduledEmployees.size);
        setAttendanceInfo({
          statusCounts,
          totalScheduled: uniqueScheduledEmployees.size,
          totalPresent
        });

      } catch (error: unknown) {
        console.error('Error fetching employee presence data:', error);
        toast.error("Failed to fetch employee presence data", {
          description: error instanceof Error ? error.message : String(error)
        });
        setLoggedInEmployees(null);
        setScheduledEmployees(null);
        setAttendanceInfo(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeePresence();
  }, [selectedBusinessId, shiftStatusService]);

  // Fetch holiday information
  useEffect(() => {
    const fetchHolidayInfo = async () => {
      if (!businessDetails?.address) return;

      try {
        const countryCode = extractCountryFromAddress(businessDetails.address);

        if (countryCode) {
          const now = new Date();
          const holidayInfo = await getHolidayInfo(countryCode, now);

          if (holidayInfo.todayHoliday) {
            setTodayHoliday(holidayInfo.todayHoliday.name);
          } else {
            setTodayHoliday(null);
          }

          if (holidayInfo.upcomingHoliday) {
            setUpcomingHoliday({
              name: holidayInfo.upcomingHoliday.name,
              daysAway: holidayInfo.upcomingHoliday.daysUntil
            });
          } else {
            setUpcomingHoliday(null);
          }
        } else {
          setTodayHoliday(null);
          setUpcomingHoliday(null);
        }
      } catch (error) {
        setTodayHoliday(null);
        setUpcomingHoliday(null);
      }
    };

    fetchHolidayInfo();
  }, [businessDetails?.address]);

  useEffect(() => {
    if (loggedInEmployees !== null && scheduledEmployees !== null && attendanceInfo) {
      // Consider attendance issues using unified status counts
      const hasIssues = attendanceInfo.statusCounts[ShiftStatus.LATE] > 0 || 
                       attendanceInfo.statusCounts[ShiftStatus.MISSED] > 0 || 
                       attendanceInfo.statusCounts[ShiftStatus.LEFT_EARLY] > 0 ||
                       attendanceInfo.statusCounts[ShiftStatus.COMPLETED_LATE] > 0;
      
      if (loggedInEmployees === scheduledEmployees && !hasIssues) {
        setStatusText('Optimal');
        setDotColorClass('bg-green-500');
      } else if (loggedInEmployees < scheduledEmployees || attendanceInfo.statusCounts[ShiftStatus.MISSED] > 0) {
        setStatusText('Understaffed');
        setDotColorClass('bg-red-500');
      } else if (hasIssues) {
        setStatusText('Issues Present');
        setDotColorClass('bg-orange-500');
      } else if (loggedInEmployees > scheduledEmployees) {
        setStatusText('Overstaffed');
        setDotColorClass('bg-blue-500');
      } else {
        setStatusText('Good');
        setDotColorClass('bg-green-500');
      }
    } else {
      setStatusText('N/A');
      setDotColorClass('bg-gray-400');
    }
  }, [loggedInEmployees, scheduledEmployees, attendanceInfo]);

  // Time is now handled by useBusinessTimeForDashboard hook

  return (
    <Card
      className="relative cursor-pointer h-full"
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex flex-row items-center justify-between">
          <div className={cn(
            "flex-1",
            isMobile ? "flex justify-between" : "grid grid-cols-2 gap-4"
          )}>
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <CardTitle className="text-sm font-medium">Attendance</CardTitle>
          </div>
          <div className={cn("h-3 w-3 rounded-full", dotColorClass)} />
        </div>
      </CardHeader>
      <CardContent className={cn(
        isMobile ? "space-y-2" : "grid grid-cols-2 gap-2"
      )}>
        {loading ? (
          <div className="col-span-2 text-center">
            <p className="text-xl font-bold">...</p>
          </div>
        ) : (
          <>
            {/* Left: Time, Status and Secondary info */}
            <div className={cn(
              "space-y-1",
              isMobile ? "" : "border-r"
            )}>
              {/* Current Time and Date */}
              <div className="flex items-baseline gap-3">
                <div className={cn(
                  "font-bold",
                  isMobile ? "text-xl" : "text-2xl"
                )}>{currentTime}</div>
                <p className="text-xs text-muted-foreground">{currentDate}</p>
              </div>
              
              {/* Business Status and Hours */}
              <div className="flex items-baseline gap-3">
                {businessStatus && (
                  <div className={cn(
                    "font-bold leading-tight",
                    isMobile ? "text-xl" : "text-2xl"
                  )}>
                    {businessStatus.statusText}
                  </div>
                )}
                
                
                {/* Business Hours - only show if status is not "Closing in xx" */}
                {businessStatus && !businessStatus.statusText.toLowerCase().includes('closing in') && (
                  <p className="text-xs text-muted-foreground">
                    {(() => {
                      const now = new Date();
                      const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                      const currentDayName = dayNames[now.getDay()];
                      const todayHours = businessDetails?.opening_hours?.find(
                        (hour) => hour.day_of_week === currentDayName
                      );

                      if (!todayHours) {
                        return "Closed all day";
                      }

                      if (todayHours.is_closed_all_day) {
                        return "Closed all day";
                      } else if (todayHours.is_24_hours) {
                        return "Open 24 hours";
                      } else if (todayHours.opening_time && todayHours.closing_time) {
                        return `${todayHours.opening_time.substring(0, 5)} - ${todayHours.closing_time.substring(0, 5)}`;
                      } else {
                        return "Hours not available";
                      }
                    })()}
                  </p>
                )}
              </div>
              
              {/* Holiday Information */}
              <div>
                {/* Upcoming Holiday */}
                {upcomingHoliday && !todayHoliday && (
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-blue-500">📅</span>
                    <span className="text-muted-foreground">
                      {upcomingHoliday.name} in {upcomingHoliday.daysAway}d
                    </span>
                  </div>
                )}
                
                {/* Today's Holiday */}
                {todayHoliday && (
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-green-500">🎉</span>
                    <span className="font-medium text-green-600">{todayHoliday}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Attendance Numbers and Details */}
            <div className="flex flex-col space-y-3">
              {/* Upper row: Attendance Numbers and Status */}
              <div className="flex items-center justify-center gap-4">
                {/* Attendance Numbers */}
                <div className="flex items-baseline gap-2">
                  <span className={cn(
                    "font-bold",
                    isMobile ? "text-xl" : "text-2xl"
                  )}>
                    {loggedInEmployees !== null ? loggedInEmployees : 'N/A'}
                  </span>
                  <span className={cn(
                    isMobile ? "text-xl" : "text-2xl"
                  )}>/</span>
                  <span className={cn(
                    "font-bold",
                    isMobile ? "text-xl" : "text-2xl"
                  )}>
                    {scheduledEmployees !== null ? scheduledEmployees : 'N/A'}
                  </span>
                </div>
                
                {/* Status */}
                <p className={cn("text-xs font-medium", {
                  "text-muted-foreground": statusText === 'N/A',
                  "text-green-600 dark:text-green-400": statusText === 'Optimal' || statusText === 'Good',
                  "text-orange-600 dark:text-orange-400": statusText === 'Issues Present' || statusText === 'Overstaffed',
                  "text-red-600 dark:text-red-400": statusText === 'Understaffed',
                })}>{statusText}</p>
              </div>
              
              {/* Lower row: Badges in two columns */}
              <div className="grid grid-cols-2 gap-2">
                {/* Render status items using unified badge system */}
                {[
                  { status: ShiftStatus.ON_TIME, label: 'on time' },
                  { status: ShiftStatus.TOO_EARLY, label: 'early' },
                  { status: ShiftStatus.LATE, label: 'late' },
                  { status: ShiftStatus.MISSED, label: 'no show' }
                ].map(({ status, label }) => {
                  const count = attendanceInfo?.statusCounts[status] || 0;
                  
                  return (
                    <div key={status} className="flex items-center gap-4">
                      <ShiftStatusBadge
                        status={status}
                        variant={StyleVariant.COMPACT}
                        showIcon={true}
                        showTooltip={false}
                        className="flex-shrink-0"
                      />
                      <span className={cn(
                        isMobile ? "text-lg" : "text-base",
                        "font-bold"
                      )}>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeePresenceCards;
